import { ApolloProvider } from '@apollo/client';
import { ApiRequestsProvider, FeatureFlagsProvider, queryClientGenerator,SiteUrlsProvider, UIActionsProvider, UserAndSiteSettingsProvider,  } from '@tableau/catalog-core';
import { RestApiServiceProvider } from '@tableau/vizportalclient-common-components';
import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';

import { CompositeSessionInfoSelectors, SessionServerSelectors, SessionSiteSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { CatalogApiRequests } from '~src/catalog/CatalogApiRequests';
import { useSelector } from '~src/settings/general-settings/utils';

import { siteUrlsSelector } from '../bootstrap/redux/selectors/SiteUrlsSelectors';
import { catalogApolloClient } from './CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from './CatalogFeatureFlagsAdapter';
import { DataCatalogUIActionsService } from './services/DataCatalogUIActionsService';

export const catalogQueryClient = queryClientGenerator();

export function CatalogProviders({ children }: { readonly children?: React.ReactNode }) {
  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);
  const siteUrls = useSelector(siteUrlsSelector);
  const areUsersVisible = !(useSelector(CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced));
  const isUserAdmin = useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  const metadataSuggestionsEnabled = useSelector(SessionSiteSelectors.metadataSuggestionsEnabled);
  const offlineHelpEnabled = useSelector(SessionServerSelectors.offlineHelpEnabled);
  const serverHelpEdition = useSelector(SessionServerSelectors.helpEdition);
  const sessionLanguage = useSelector(SessionUserSelectors.language);
  const serverPlatform = useSelector(SessionServerSelectors.version);
  const helpUrlParams = {
    offlineHelpEnabled,
    serverHelpEdition,
    sessionLanguage,
    serverPlatform: serverPlatform?.platform ?? '',
    serverVersion: {
      major: serverPlatform?.externalVersion.major ?? '0',
      minor: serverPlatform?.externalVersion.minor ?? '0'
    }
  };
  const userAndSiteSettings = {
    areUsersVisible,
    isUserAdmin,
    helpUrlParams,
    metadataSuggestionsEnabled,
  };

  return (
    <>
      <ApiRequestsProvider value={CatalogApiRequests}>
        <FeatureFlagsProvider value={catalogFeatureFlagsAdapter()}>
          <UIActionsProvider value={DataCatalogUIActionsService.instance()}>
            <ApolloProvider client={catalogApolloClient}>
              <SiteUrlsProvider value={siteUrls}>
                <RestApiServiceProvider params={{ siteLuid }}>
                  <QueryClientProvider client={catalogQueryClient}>
                  <UserAndSiteSettingsProvider value={userAndSiteSettings}>
                    {children}
                  </UserAndSiteSettingsProvider>
                  </QueryClientProvider>
                </RestApiServiceProvider>
              </SiteUrlsProvider>
            </ApolloProvider>
          </UIActionsProvider>
        </FeatureFlagsProvider>
      </ApiRequestsProvider>
    </>
  );
}
